import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs/operators';
import { ConstructAPI } from 'src/app/API/constructAPI';
import { RestApiService } from 'src/app/API/restapi';
import { SharedService } from 'src/app/Shared/shared-service.service';
import { FileInterface } from 'src/model/file.interface';
import { IAPIReserveGroup, ReserveGroup, rgDistribution } from 'src/model/reserveGroup.model';
import { FileAdapter, FileUrlBuilder } from './file.service';

export interface rgOptions {
  profileId?: number,
  companyId?: number,
  accepted?: boolean,
  myAccepted?: boolean,
  startDate?: Date,
  endDate?: Date,
  valid?: boolean,
  checked?: boolean,
  pending?: boolean,
  includePending?: boolean,
  lastId?: number,
  limit?: number,
  sortASC?: boolean,
}

@Injectable({
  providedIn: 'root'
})
export class ReserveGroupAdapter {

  constructor() { }

  adaptRGDistribution(item: any): rgDistribution {
    return new rgDistribution(
      item.id,
      item.companyId,
      item.amount,
      item.companyCode,
      (item.acceptedOn && new Date(item.acceptedOn)),
      (item.transactionDate && new Date(item.transactionDate))
    )
  }

  adapt(item: any): ReserveGroup {
    const distribution = item.distribution?.map(d => this.adaptRGDistribution(d));

    return new ReserveGroup(
      distribution,
      item.id,
      item.requestedBy,
      item.requestedOn,
      item.sharedProfileId,
      item.accepted,
      item.total,
      item.valid
    )
  }

  toApiModel(rg: ReserveGroup): IAPIReserveGroup {
    return {
      id: rg.ID,
      distribution: rg.Distribution,
      total: rg.Total,
      accepted: rg.Accepted,
      valid: rg.Valid,
      requestedBy: rg.RequestedBy,
      requestedOn: rg.RequestedOn,
      sharedProfileId: rg.SharedProfileId
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class ReserveGroupService {
  private api = inject(RestApiService);
  private constructApi = inject(ConstructAPI);
  private adapter = inject(ReserveGroupAdapter);
  private sharedService = inject(SharedService);
  private fileAdapter = inject(FileAdapter);
  private fileUrl = inject(FileUrlBuilder);

  getAllReserveGroup(o?: rgOptions): Promise<ReserveGroup[]> {
    o?.startDate?.setHours(0, 0, 0, 0);
    const endDate = (o?.endDate !== undefined) ? this.sharedService.getLastMsOfDate(o?.endDate) : undefined;
    return new Promise<ReserveGroup[]>((resolve, reject) => {
      this.api.get<ReserveGroup[]>(this.constructApi.getAllReserveGroup(o?.profileId, o?.companyId, o?.accepted, o?.myAccepted, o?.startDate?.toISOString(), endDate?.toISOString(), o?.valid, o?.checked, o?.pending, o?.includePending, o?.lastId, o?.limit, o?.sortASC)).pipe(
        map(data => data.map(d => this.adapter.adapt(d))),
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to get all reserve group: ', err);
          reject(err);
        }
      })
    })
  }

  getReserveGroupDetails(id: number): Promise<ReserveGroup> {
    return new Promise<ReserveGroup>((resolve, reject) => {
      this.api.get<ReserveGroup>(this.constructApi.getReserveGroupDetails(id)).pipe(
        map(data => this.adapter.adapt(data)),
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to get reserve group details: ', err);
          reject(err);
        }
      })
    })
  }

  getAllProfileReserveGroups(profileId: number): Promise<ReserveGroup[]> {
    return new Promise<ReserveGroup[]>((resolve, reject) => {
      this.api.get<ReserveGroup[]>(this.constructApi.getAllProfileReserveGroups(profileId)).pipe(
        map(data => data.map(d => this.adapter.adapt(d))),
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to get all profile reserve groups: ', err);
          reject(err);
        }
      })
    })
  }

  putSubmitReserveGroup(id: number) {
    return new Promise((resolve, reject) => {
      this.api.put(this.constructApi.getSubmitReserveGroup(id)).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to submit reserve group: ', err);
          reject(err);
        }
      })
    })
  }

  postAcceptReserveGroup(id: number, remark: string, accountId: number, dateCreated: Date) {
    const data = {
      remark: remark,
      accountId: Number(accountId),
      dateCreated: dateCreated.toISOString()
    }
    return new Promise((resolve, reject) => {
      this.api.post(this.constructApi.getAcceptReserveGroup(id), data).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to accept reserve group: ', err);
          reject(err);
        }
      })
    })
  }

  deleteReserveGroup(id: number) {
    return new Promise((resolve, reject) => {
      this.api.delete(this.constructApi.getDeleteReserveGroup(id)).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to delete reserve group: ', err);
          reject(err);
        }
      })
    })
  }

  getReserveGroupAllFiles(id: number): Promise<FileInterface[]> {
    return new Promise<FileInterface[]>((resolve, reject) => {
      this.api.get<FileInterface[]>(this.constructApi.getReserveGroupAllFiles(id)).pipe(
        map(data => data.map(d => this.fileAdapter.adapt(d, this.fileUrl.buildReserveGroupFile(id))))
      ).subscribe({
        next: response => resolve(response),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to get reserve group files: ', err);
          reject(err);
        }
      })
    })
  }
}
